import type { Key } from 'react'
import { Button, Input, Select, Space } from 'antd'
import { SearchOutlined } from '@ant-design/icons'

/** Case-insensitive substring match for column filters (client-side). */
export function textMatchesColumnFilter(
  value: boolean | Key,
  text: unknown
): boolean {
  if (value === '' || value === null || value === undefined) return true
  return String(text ?? '')
    .toLowerCase()
    .includes(String(value).toLowerCase())
}

/**
 * LEAP Seekho–style column search: dropdown with Input + Search / Reset,
 * header filter icon turns brand red (#DC2925) with white icon when active.
 */
export function getColumnSearchProps<T>(
  dataIndexLabel: string,
  onFilterFn?: (value: boolean | Key, record: T) => boolean
) {
  return {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: {
      setSelectedKeys: (keys: React.Key[]) => void
      selectedKeys: React.Key[]
      confirm: (param?: { closeDropdown: boolean }) => void
      clearFilters?: () => void
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          placeholder={`Search ${dataIndexLabel}`}
          value={(selectedKeys[0] as string) ?? ''}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters?.()
              confirm()
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <div
        className={
          filtered
            ? 'bg-[#DC2925] p-0.5 rounded flex items-center justify-center transition-colors'
            : 'transition-colors'
        }
      >
        <SearchOutlined style={{ color: filtered ? '#FFFFFF' : undefined }} />
      </div>
    ),
    onFilter: onFilterFn,
  }
}

export type EnumFilterOption = { label: string; value: string; disabled?: boolean }

/**
 * Select-based filter for enum columns (type, status, …). Same active icon styling as Seekho.
 */
export function getColumnEnumFilterProps<T>(
  label: string,
  options: EnumFilterOption[],
  getValue: (record: T) => string
) {
  return {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: {
      setSelectedKeys: (keys: React.Key[]) => void
      selectedKeys: React.Key[]
      confirm: (param?: { closeDropdown: boolean }) => void
      clearFilters?: () => void
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Select
          allowClear
          placeholder={label}
          value={selectedKeys[0] as string | undefined}
          onChange={(v) => setSelectedKeys(v != null && v !== '' ? [v] : [])}
          style={{ width: '100%', marginBottom: 8, display: 'block' }}
          options={options}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            Apply
          </Button>
          <Button
            onClick={() => {
              clearFilters?.()
              confirm()
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <div
        className={
          filtered
            ? 'bg-[#DC2925] p-0.5 rounded flex items-center justify-center transition-colors'
            : 'transition-colors'
        }
      >
        <SearchOutlined style={{ color: filtered ? '#FFFFFF' : undefined }} />
      </div>
    ),
    onFilter: (value: boolean | Key, record: T) => {
      if (value === '' || value === null || value === undefined) return true
      return String(getValue(record) ?? '').trim().toUpperCase() === String(value).trim().toUpperCase()
    },
  }
}
